import React, { useState, useEffect } from 'react';
import GenericModal from '@ui-components/Modal/GenericModal';
import GenericButton from '@ui-components/Button/GenericButton';
import GenericInput from '@ui-components/Input/GenericInput';
import GenericDropdown from '@ui-components/Dropdown/GenericDropdown';
import type { FortiFlexPack } from '../hooks/useFortiFlexData';

interface PackFormModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (data: PackFormData) => void;
    pack?: FortiFlexPack | null;
    loading?: boolean;
}

export interface PackFormData {
    packNumber: string;
    packIndex: number;
    totalPoints: number;
    startDate: string;
    expirationDate: string;
    notes?: string;
    status?: string;
}

const INITIAL: PackFormData = {
    packNumber: '',
    packIndex: 1,
    totalPoints: 0,
    startDate: '',
    expirationDate: '',
    notes: '',
};

const STATUS_OPTIONS = ['Active', 'Cancelled', 'Expired'];
const STATUS_MAP: Record<string, string> = {
    Active: 'active',
    Cancelled: 'cancelled',
    Expired: 'expired',
};
const STATUS_REVERSE: Record<string, string> = {
    active: 'Active',
    cancelled: 'Cancelled',
    expired: 'Expired',
};

const PackFormModal: React.FC<PackFormModalProps> = ({
    isOpen,
    onClose,
    onSubmit,
    pack,
    loading,
}) => {
    const [form, setForm] = useState<PackFormData>(INITIAL);
    const isEdit = Boolean(pack);

    useEffect(() => {
        if (pack) {
            setForm({
                packNumber: pack.packNumber,
                packIndex: pack.packIndex,
                totalPoints: pack.totalPoints,
                startDate: pack.startDate,
                expirationDate: pack.expirationDate,
                notes: pack.notes || '',
                status: pack.status,
            });
        } else {
            setForm(INITIAL);
        }
    }, [pack, isOpen]);

    const onChange = (field: keyof PackFormData, value: string | number) => {
        setForm((prev) => ({ ...prev, [field]: value }));
    };

    const handleSubmit = () => {
        onSubmit(form);
    };

    return (
        <GenericModal
            isOpen={isOpen}
            onClose={onClose}
            title={isEdit ? 'Edit Pack' : 'Add Pack'}
            size="md"
            footer={
                <>
                    <GenericButton
                        variant="secondary"
                        size="md"
                        label="Cancel"
                        onClick={onClose}
                    />
                    <GenericButton
                        variant="brand"
                        size="md"
                        label={loading ? 'Saving...' : isEdit ? 'Update' : 'Create'}
                        onClick={handleSubmit}
                        disabled={loading || !form.packNumber || !form.totalPoints}
                        loading={loading}
                    />
                </>
            }
        >
            <div className="space-y-4">
                <GenericInput
                    label="Pack Number"
                    value={form.packNumber}
                    onChange={(v) => onChange('packNumber', v)}
                    disabled={isEdit}
                />
                <div className="grid grid-cols-2 gap-4">
                    <GenericInput
                        label="Pack Index"
                        type="number"
                        value={form.packIndex}
                        onChange={(v) => onChange('packIndex', Number.parseInt(v, 10) || 0)}
                    />
                    <GenericInput
                        label="Total Points"
                        type="number"
                        value={form.totalPoints}
                        onChange={(v) => onChange('totalPoints', Number.parseInt(v, 10) || 0)}
                    />
                </div>
                <div className="grid grid-cols-2 gap-4">
                    <GenericInput
                        label="Start Date"
                        type="text"
                        placeholder="YYYY-MM-DD"
                        value={form.startDate}
                        onChange={(v) => onChange('startDate', v)}
                        disabled={isEdit}
                    />
                    <GenericInput
                        label="Expiration Date"
                        type="text"
                        placeholder="YYYY-MM-DD"
                        value={form.expirationDate}
                        onChange={(v) => onChange('expirationDate', v)}
                        disabled={isEdit}
                    />
                </div>
                {isEdit && (
                    <div className="flex flex-col space-y-2">
                        <label className="text-sm font-medium text-semantic-text-primary">
                            Status
                        </label>
                        <GenericDropdown
                            items={STATUS_OPTIONS}
                            value={STATUS_REVERSE[form.status || 'active'] || 'Active'}
                            onSelect={(v) => onChange('status', STATUS_MAP[v] || v.toLowerCase())}
                            defaultLabel="Select Status"
                        />
                    </div>
                )}
                <GenericInput
                    label="Notes"
                    value={form.notes || ''}
                    onChange={(v) => onChange('notes', v)}
                    placeholder="Optional notes"
                />
            </div>
        </GenericModal>
    );
};

export default PackFormModal;
